import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
  memo,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import BackgroundImage from "../../../../components/ui/BackgroundImage";
import inputDefaultBg from "../../assets/images/input-bg.png";
import inputActiveBg from "../../assets/images/input-active-bg.png";
import blurredBg from "../../assets/images/blurred-bg.png";
import transparentBg from "../../../clothing-store/assets/images/transparent-bg.webp";
import {
  IoIosArrowBack as ArrowBack,
  IoIosArrowForward as ArrowNext,
} from "react-icons/io";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";
import clickSound from "../../../../assets/audio/click.wav";
import swipeSound from "../../../../assets/audio/swipe.wav";

export type FieldOption = {
  name: string;
  value: string | number;
  [key: string]: any;
};
export type Value = string | FieldOption | number;

export interface FieldProps {
  className?: string;
  label?: string;
  description?: string;
  placeholder?: string;
  iconSrc?: string;
  initialValue?: Value;
  defaultValue?: Value;
  checkboxLabel?: string;
  type: "input" | "checkbox" | "slider" | "stepper" | "select";
  checked?: boolean;
  onCheckedChange?: () => void;
  onChange?: (value: Value) => void;
  max?: number;
  min?: number;
  options?: FieldOption[];
}

// ── Shared animation constants ───────────────────────────────────────────────
const BTN_TAP = { scale: 0.86 };
const BTN_HOVER = { scale: 1 };
const BTN_SPRING = { type: "spring", stiffness: 420, damping: 22 } as const;

const headerItem: any = {
  hidden: { opacity: 0, y: -5 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, delay: i * 0.07, ease: "easeOut" },
  }),
};
// ─────────────────────────────────────────────────────────────────────────────

const Field: React.FC<FieldProps> = ({
  className,
  label,
  description,
  iconSrc,
  placeholder,
  type = "input",
  max = 100,
  initialValue,
  min = 0,
  checked,
  options = [],
  onChange,
  onCheckedChange,
  checkboxLabel,
}) => {
  const [value, setValue] = useState<Value>(initialValue ?? "");
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = useMemo(() => value !== "", [value]);

  // ── Input animations ─────────────────────────────────────────────────────
  const [scanKey, setScanKey] = useState(0);
  const [showScan, setShowScan] = useState(false);
  const [typingKey, setTypingKey] = useState(0);
  const [showTypingFlash] = useState(false);
  const isFocusedRef = useRef(false);
  const isFirstRender = useRef(true);

  const { playEffect } = useAudioEffects();

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isFocusedRef.current) return;
    setTypingKey((k) => k + 1);
  }, [value]);

  const borderColor = useMemo(
    () =>
      isFocused
        ? (["#48A7FF", "#85caff", "#48A7FF"] as unknown as string)
        : hasValue
          ? "#48A7FF"
          : "#697682",
    [isFocused, hasValue],
  );
  // ─────────────────────────────────────────────────────────────────────────

  // ── Slider ───────────────────────────────────────────────────────────────
  const sliderTrackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const [sliderDraft, setSliderDraft] = useState<number | null>(null);
  const [isSliderActive, setIsSliderActive] = useState(false);
  const [committedKey, setCommittedKey] = useState(0);

  const displayValue = sliderDraft ?? Number(value);

  const sliderPercent = useMemo(() => {
    const num = isNaN(displayValue) ? min : displayValue;
    return Math.min(100, Math.max(0, ((num - min) / (max - min)) * 100));
  }, [displayValue, min, max]);

  const getRawValueFromClientX = useCallback(
    (clientX: number): number => {
      const el = sliderTrackRef.current;
      if (!el) return min;
      const rect = el.getBoundingClientRect();
      const ratio = Math.min(
        1,
        Math.max(0, (clientX - rect.left) / rect.width),
      );
      return min + ratio * (max - min);
    },
    [min, max],
  );

  const handleSliderPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      isDragging.current = true;
      setIsSliderActive(true);
      setSliderDraft(getRawValueFromClientX(e.clientX));
    },
    [getRawValueFromClientX],
  );

  const handleSliderPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging.current) return;
      setSliderDraft(getRawValueFromClientX(e.clientX));
    },
    [getRawValueFromClientX],
  );

  const handleSliderPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging.current) return;
      isDragging.current = false;
      setIsSliderActive(false);
      // ── Decimal: keep 2 decimal places, no rounding to integer ──────────
      const decimal = parseFloat(getRawValueFromClientX(e.clientX).toFixed(2));
      setCommittedKey((k) => k + 1);
      setSliderDraft(null);
      handleChange(Math.min(max, Math.max(min, decimal)));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getRawValueFromClientX, min, max],
  );
  // ─────────────────────────────────────────────────────────────────────────

  // ── Select / Stepper ─────────────────────────────────────────────────────
  const [selectDir, setSelectDir] = useState<1 | -1>(1);
  const [selectKey, setSelectKey] = useState(0);
  const [stepperKey, setStepperKey] = useState(0);
  // ─────────────────────────────────────────────────────────────────────────

  const handleChange = useCallback(
    (val: Value) => {
      setValue(val);
      onChange?.(val);
    },
    [onChange],
  );

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
    setIsFocused(true);
    setScanKey((k) => k + 1);
    setShowScan(true);
    setTimeout(() => setShowScan(false), 650);
  }, []);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    setIsFocused(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value),
    [handleChange],
  );

  const slideBack = useCallback(() => {
    playEffect(swipeSound, { inBackground: true, volume: 0.5 });
    const idx = options.findIndex(
      (o) => o.value === (value as FieldOption)?.value,
    );
    if (idx !== -1) {
      setSelectDir(-1);
      setSelectKey((k) => k + 1);
      handleChange(options[idx === 0 ? options.length - 1 : idx - 1]);
    }
  }, [options, value, handleChange]);

  const slideNext = useCallback(() => {
    playEffect(swipeSound, { inBackground: true, volume: 0.5 });
    const idx = options.findIndex(
      (o) => o.value === (value as FieldOption)?.value,
    );
    if (idx !== -1) {
      setSelectDir(1);
      setSelectKey((k) => k + 1);
      handleChange(options[idx === options.length - 1 ? 0 : idx + 1]);
    }
  }, [options, value, handleChange]);

  const handleStepperDecrement = useCallback(() => {
    playEffect(swipeSound, { inBackground: true, volume: 0.5 });

    setStepperKey((k) => k + 1);
    setValue((v) => {
      const next = Number(v) === min ? max : Number(v) - 1;
      onChange?.(next);
      return next;
    });
  }, [min, max, onChange]);

  const handleStepperIncrement = useCallback(() => {
    playEffect(swipeSound, { inBackground: true, volume: 0.5 });

    setStepperKey((k) => k + 1);
    setValue((v) => {
      const next = Number(v) === max ? min : Number(v) + 1;
      onChange?.(next);
      return next;
    });
  }, [min, max, onChange]);

  const includesHeader = useMemo(
    () => label !== undefined || description !== undefined,
    [label, description],
  );

  const inputBg = useMemo(
    () => (isFocused || hasValue ? inputActiveBg : inputDefaultBg),
    [isFocused, hasValue],
  );

  const checkboxBg = useMemo(
    () => (checked ? inputActiveBg : inputDefaultBg),
    [checked],
  );

  return (
    <div className={`overflow-hidden ${className}`}>
      {includesHeader && (
        <div className="mb-2">
          {label && (
            <motion.h2
              className="text-white leading-6 text-[1.25rem] font-medium"
              custom={0}
              variants={headerItem}
              initial="hidden"
              animate="visible"
            >
              {label}
            </motion.h2>
          )}
          {description && (
            <motion.p
              className="text-white/50 text-[.9rem]"
              custom={1}
              variants={headerItem}
              initial="hidden"
              animate="visible"
            >
              {description}
            </motion.p>
          )}
        </div>
      )}

      {/* ── INPUT ──────────────────────────────────────────────────────── */}
      {type === "input" && (
        <motion.div
          transition={
            showScan
              ? { duration: 0.6, times: [0, 0.3, 1], ease: "easeOut" }
              : { duration: 0.3 }
          }
          className="overflow-hidden relative"
        >
          <AnimatePresence>
            {showScan && (
              <motion.span
                key={`scan-${scanKey}`}
                className="absolute inset-y-0 w-[55%] pointer-events-none z-10"
                style={{
                  background:
                    "linear-gradient(to right, transparent, rgba(72,167,255,0.22), transparent)",
                }}
                initial={{ x: "-100%" }}
                animate={{ x: "300%" }}
                transition={{ duration: 0.55, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>

          {showTypingFlash && (
            <motion.span
              key={`type-${typingKey}`}
              className="absolute inset-y-0 right-0 w-[45%] pointer-events-none z-10"
              style={{
                background:
                  "linear-gradient(to left, rgba(72,167,255,0.2), transparent)",
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0] }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            />
          )}

          <BackgroundImage imageSrc={inputBg} imageClassName="!object-fill">
            <motion.input
              onFocus={handleFocus}
              onBlur={handleBlur}
              max={max}
              min={min}
              placeholder={placeholder}
              type="text"
              value={value as string}
              onChange={handleInputChange}
              animate={{ borderLeftColor: borderColor }}
              transition={
                isFocused
                  ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
                  : { duration: 0.3 }
              }
              className="outline-none bg-transparent w-full h-full text-center px-7 min-h-[60px] text-white placeholder:text-white/50 border-l-[#697682]"
            />
          </BackgroundImage>
        </motion.div>
      )}

      {/* ── SELECT ─────────────────────────────────────────────────────── */}
      {type === "select" && (
        <BackgroundImage
          imageSrc={blurredBg}
          imageClassName="!object-fill"
          className="p-2 px-3"
        >
          <div className="flex items-center justify-between space-x-3">
            <motion.button
              title="Back"
              onClick={slideBack}
              whileHover={BTN_HOVER}
              whileTap={BTN_TAP}
              transition={BTN_SPRING}
              className="p-2 rounded-md min-w-[30px] min-h-[30px] flex items-center justify-center border border-[#56F5FF]/40 bg-gradient-to-r from-[#287384]/50 to-[#07181A]/50 text-[#56F5FF]"
            >
              <ArrowBack />
            </motion.button>

            <div
              className="relative flex flex-1 justify-center overflow-hidden"
              style={{ minHeight: "1.5rem" }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.p
                  key={selectKey}
                  className="absolute text-[0.95rem] text-white"
                  initial={{ opacity: 0, y: selectDir * -14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: selectDir * 14 }}
                  transition={{ duration: 0.1, ease: "easeOut" }}
                >
                  {(value as FieldOption)?.name}
                </motion.p>
              </AnimatePresence>
            </div>

            <motion.button
              title="Next"
              onClick={slideNext}
              whileHover={BTN_HOVER}
              whileTap={BTN_TAP}
              transition={BTN_SPRING}
              className="p-2 rounded-md min-w-[30px] min-h-[30px] flex items-center justify-center border border-[#56F5FF]/40 bg-gradient-to-r from-[#287384]/50 to-[#07181A]/50 text-[#56F5FF]"
            >
              <ArrowNext size={18} />
            </motion.button>
          </div>
        </BackgroundImage>
      )}

      {/* ── CHECKBOX ───────────────────────────────────────────────────── */}
      {type === "checkbox" && (
        <BackgroundImage imageSrc={checkboxBg} imageClassName="!object-fill">
          <motion.div
            className="flex items-center justify-between min-h-[60px] cursor-pointer relative"
            onClick={() => {
              if (onCheckedChange) {
                playEffect(clickSound);
                onCheckedChange();
              }
            }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 500, damping: 28 }}
          >
            <p className="absolute left-8 top-[50%] translate-y-[-50%] text-white text-[0.95rem]">
              {checkboxLabel}
            </p>

            <AnimatePresence>
              {checked && (
                <motion.span
                  key="checkbox-glow"
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to right, rgba(72,167,255,0.1), transparent 70%)",
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </AnimatePresence>

            {iconSrc && (
              <motion.img
                src={iconSrc}
                alt="icon"
                className="w-full h-full max-w-[140px] max-h-[70px] ml-auto select-none"
                draggable={false}
                animate={{
                  opacity: checked ? 1 : 0.85,
                  scale: checked ? 1 : 0.93,
                  filter: checked ? "brightness(1)" : "brightness(0.55)",
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            )}
          </motion.div>
        </BackgroundImage>
      )}

      {/* ── STEPPER ────────────────────────────────────────────────────── */}
      {type === "stepper" && (
        <BackgroundImage imageSrc={transparentBg} imageClassName="!object-fill">
          <div className="p-3 px-4 min-h-[50px] 1440px:min-h-[60px] flex items-center justify-between">
            <motion.button
              title="Less"
              onClick={handleStepperDecrement}
              whileHover={BTN_HOVER}
              whileTap={BTN_TAP}
              transition={BTN_SPRING}
              className="p-2 rounded-md min-w-[30px] min-h-[30px] flex items-center justify-center border border-[#56F5FF]/40 bg-gradient-to-r from-[#287384]/50 to-[#07181A]/50 text-[#56F5FF]"
            >
              <ArrowBack />
            </motion.button>

            <p className="text-[0.95rem] text-white">
              <AnimatePresence mode="wait">
                <motion.span
                  key={stepperKey}
                  initial={{ opacity: 0.3, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0.3, y: -5 }}
                  transition={{ duration: 0.1 }}
                >
                  {value?.toString()}
                </motion.span>
              </AnimatePresence>
              /{max}
            </p>

            <motion.button
              title="More"
              onClick={handleStepperIncrement}
              whileHover={BTN_HOVER}
              whileTap={BTN_TAP}
              transition={BTN_SPRING}
              className="p-2 rounded-md min-w-[30px] min-h-[30px] flex items-center justify-center border border-[#56F5FF]/40 bg-gradient-to-r from-[#287384]/50 to-[#07181A]/50 text-[#56F5FF]"
            >
              <ArrowNext size={18} />
            </motion.button>
          </div>
        </BackgroundImage>
      )}

      {/* ── SLIDER ─────────────────────────────────────────────────────── */}
      {type === "slider" && (
        <div className="select-none">
          <div className="flex items-center justify-between mb-1.5 px-1">
            <span className="text-[0.85rem] font-[500] text-white/40">
              {min}
            </span>
            <motion.span
              key={committedKey}
              className="text-[1rem] font-[500] text-white tabular-nums"
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              {/* ── Show 2 decimal places during drag and after commit ── */}
              {sliderDraft !== null
                ? sliderDraft.toFixed(2)
                : Number(value).toFixed(2)}
              <span className="text-white/40">/{max}</span>
            </motion.span>
            <span className="text-[0.85rem] font-[500] text-white/40">
              {max}
            </span>
          </div>

          <div
            ref={sliderTrackRef}
            className="group relative flex h-5 w-full cursor-grab items-center active:cursor-grabbing"
            onPointerDown={handleSliderPointerDown}
            onPointerMove={handleSliderPointerMove}
            onPointerUp={handleSliderPointerUp}
            onPointerCancel={handleSliderPointerUp}
          >
            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <motion.div
                className="absolute left-0 top-0 h-full rounded-full"
                style={{
                  background:
                    "linear-gradient(90deg, #0e9f9f 0%, #2dd4bf 100%)",
                }}
                animate={{
                  width: `${sliderPercent}%`,
                  boxShadow: isSliderActive
                    ? "0 0 16px rgba(45, 212, 191, 0.8)"
                    : "0 0 8px rgba(45, 212, 191, 0.5)",
                }}
                transition={
                  isDragging.current
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 280, damping: 28 }
                }
              />
            </div>

            <div
              className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2"
              style={{
                left: `clamp(0px, calc(${sliderPercent}% - 8px), calc(100% - 16px))`,
                transition: isDragging.current ? "none" : "left 150ms ease",
              }}
            >
              <motion.div
                className="w-full h-full rounded-full border-2 border-white bg-white"
                animate={{
                  scale: isSliderActive ? 1.25 : 1,
                  boxShadow: isSliderActive
                    ? "0 0 0 5px rgba(72,167,255,0.28), 0 2px 8px rgba(0,0,0,0.45)"
                    : "0 2px 6px rgba(0,0,0,0.3)",
                }}
                transition={
                  isDragging.current
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 380, damping: 22 }
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(Field);
